import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  createZadCustomer,
  fetchZadCustomerById,
  fetchZadCustomers,
  searchZadMaarefatiUsers,
  updateZadCustomer,
  updateZadCustomerStatus,
} from './service'
import {FetchZadCustomersResponse, ZadCustomerState} from './types'

const initialState: ZadCustomerState = {
  customers: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  maarefatiSearchLoading: false,
  selectedCustomer: null,
  studentResults: [],
  parentResults: [],
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadCustomerState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadCustomerState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

const zadCustomerSlice = createSlice({
  name: 'zadCustomers',
  initialState,
  reducers: {
    clearZadCustomerError: (state: ZadCustomerState) => {
      clearError(state)
    },
    clearSelectedZadCustomer: (state: ZadCustomerState) => {
      state.selectedCustomer = null
    },
    clearZadMaarefatiSearch: (state: ZadCustomerState) => {
      state.studentResults = []
      state.parentResults = []
      state.maarefatiSearchLoading = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadCustomers.pending, (state: ZadCustomerState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(
      fetchZadCustomers.rejected,
      (state: ZadCustomerState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.listLoading = false
        state.listInitialized = true
      }
    )
    builder.addCase(
      fetchZadCustomers.fulfilled,
      (state: ZadCustomerState, action: PayloadAction<FetchZadCustomersResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.customers = action.payload.customers
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadCustomerById.pending, (state: ZadCustomerState) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadCustomerById.rejected, (state: ZadCustomerState, action: PayloadAction<any>) => {
      state.actionLoading = false
      applyRejected(state, action)
    })
    builder.addCase(fetchZadCustomerById.fulfilled, (state: ZadCustomerState, action) => {
      state.actionLoading = false
      state.selectedCustomer = action.payload.customer
    })

    builder.addCase(createZadCustomer.pending, (state: ZadCustomerState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      createZadCustomer.rejected,
      (state: ZadCustomerState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(createZadCustomer.fulfilled, (state: ZadCustomerState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadCustomer.pending, (state: ZadCustomerState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadCustomer.rejected,
      (state: ZadCustomerState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadCustomer.fulfilled, (state: ZadCustomerState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadCustomerStatus.pending, (state: ZadCustomerState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadCustomerStatus.rejected,
      (state: ZadCustomerState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadCustomerStatus.fulfilled, (state: ZadCustomerState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(searchZadMaarefatiUsers.pending, (state: ZadCustomerState) => {
      state.maarefatiSearchLoading = true
    })
    builder.addCase(searchZadMaarefatiUsers.rejected, (state: ZadCustomerState) => {
      state.maarefatiSearchLoading = false
    })
    builder.addCase(searchZadMaarefatiUsers.fulfilled, (state: ZadCustomerState, action) => {
      state.maarefatiSearchLoading = false
      if (action.payload.type === 'STUDENT') {
        state.studentResults = action.payload.results
      } else {
        state.parentResults = action.payload.results
      }
    })
  },
})

export default zadCustomerSlice.reducer
export const actions = zadCustomerSlice.actions
export {initialState as zadCustomerInitialState}
